import posthog from 'posthog-js/dist/module.full.no-external'

import type { EventTracker } from '@repo/observability/events'

import { logger } from '@/lib/observability/logger'
import { isDebugModeEnabled } from '@/lib/observability/logger/utils/debug-mode'

import { isAnalyticsOptedOut } from './consent'
import type { AnalyticsEventMap, AnalyticsEventName } from './events'
import { notifyAnalyticsIdentityChanged } from './identity'
import { registerSessionProperties } from './session'

type AnalyticsPersonProperties = Record<string, string | number | boolean>

/**
 * Returns whether PostHog accepted the event into its queue. `capture` returns
 * undefined when it drops the event outright (client not initialized, opted
 * out, rate-limited) rather than throwing, so both paths count as a failure.
 * Acceptance is not delivery: capture batches, and a queued event can still be
 * lost. Most callers ignore the result.
 */
export function trackAnalyticsEvent<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventMap[E]
) {
  return captureAnalyticsEvent(event, { ...properties })
}

function captureAnalyticsEvent(event: string, properties?: Record<string, unknown>): boolean {
  try {
    // Debug-only console mirror, like breadcrumbs: E2E runs never init PostHog.
    if (isDebugModeEnabled()) console.debug('[analytics]', event, JSON.stringify(properties))
    return posthog.capture(event, properties) !== undefined
  } catch {
    logger.warn('Failed to track analytics event', {
      extra: { event }
    })
    return false
  }
}

/** Untyped PostHog sink for shared `@repo/*` providers that emit their own event names (ActionFlow). */
export const analyticsEventTracker: EventTracker = {
  track: (event, properties) => {
    captureAnalyticsEvent(event, properties)
  }
}

export function identifyAnalyticsPerson(distinctId: string, properties: AnalyticsPersonProperties) {
  try {
    posthog.identify(distinctId, properties)
  } catch {
    logger.warn('Failed to identify analytics person')
  }

  notifyAnalyticsIdentityChanged()
}

/** PostHog's `$session_id`, shared by every tab; undefined before init. */
export function getAnalyticsSessionId(): string | undefined {
  try {
    return posthog.get_session_id() || undefined
  } catch {
    return undefined
  }
}

/** Updates properties on the currently identified PostHog person. */
export function setAnalyticsPersonProperties(properties: AnalyticsPersonProperties) {
  try {
    posthog.setPersonProperties(properties)
  } catch {
    logger.warn('Failed to set analytics person properties')
  }
}

/**
 * Drops the current PostHog person. `reset()` also clears session
 * persistence and the stored consent, so the page-load session properties are
 * re-registered and the user's persisted analytics preference is re-asserted
 * (a mid-session opt-out must survive a disconnect or account switch).
 */
export function resetAnalyticsPerson() {
  try {
    posthog.reset()
    if (isAnalyticsOptedOut()) {
      posthog.opt_out_capturing()
    } else {
      posthog.opt_in_capturing({ captureEventName: null })
    }
  } catch {
    logger.warn('Failed to reset analytics person')
  }

  registerSessionProperties()
  notifyAnalyticsIdentityChanged()
}
